import { axiosInstance } from "@/lib/axios-instance";

interface CreateCompanyPitchboxProps {
  userId: string;
  workspaceId: string;
  companyName: string;
  companyWebsite: string;
}

interface changeStatusResponse {
  status: string;
  message?: string;
}

export interface RecentDeal {
  id: string;
  name: string;
  website?: string;
  status: string;
  /** ISO date the deal was added. */
  added_on: string;
  /** Avatar file(s) for CompanyAvatar — only file_type/file_url are used. */
  company_files: { file_type: string; file_url?: string }[];
}

export interface DashboardData {
  status: string;
  data: {
    total_deals: number;
    reports_generated: number;
    total_reviews: number;
    portfolio_companies: number;
    leads_per_month: number[];
    pipeline_stages?: {
      new?: number;
      review?: number;
      deepdiligence?: number;
      firstdiligence?: number;
      offer?: number;
    };
    recent_metrics: {
      total_deals: number;
      reports_generated: number;
      total_reviews: number;
      portfolio_companies: number;
    };
    recent_deals?: RecentDeal[];
  };
}

export interface addDealNoteResponse {
  status: string;
  application: {
    reports_in_progress: {
      deal_memo: boolean;
    };
    _id: string;
    applicant_company: string;
    pertains_to_opportunity: string;
    action_type: string;
    application_status: string;
    created_on: string;
    deal_info: Array<{
      field_name: string;
      field_value: string | number | string[];
      field_state: string;
    }>;
    deal_notes: Array<{
      _id: string;
      note: string;
      created_by_user: string;
      created_by_user_id: string;
      created_on: string;
      tags?: string[];
    }>;
    __v: number;
    fundraising?: {
      total_amount: string;
      committed_amount: string;
      lead_investor: string;
    };
    website_summary?: string;
    changed_by: string;
  };
}

export const getDashboardData = async (
  workspaceId: string
): Promise<DashboardData> => {
  const response = await axiosInstance.get("/fetch_dashboard_for_opportunity", {
    params: {
      opportunity_id: workspaceId,
    },
  });
  return response.data;
};

interface FlaggedCompanyResponse {
  status: string;
  result: {
    _id: string;
    flagged_company: string;
    opportunity: string;
    flagged_by: string;
    created_on: string;
    __v: number;
  };
}

interface DeleteFlaggedCompanyResponse {
  status: string;
}

export const dealsApi = {
  getDealById: async (workspaceId: string, companyId: string) => {
    const response = await axiosInstance.get("/fetch_single_deal", {
      params: {
        opportunity_id: workspaceId,
        company_id: companyId,
      },
    });
    if (response.data.status !== "success") {
      throw new Error(response.data.message || "Failed to fetch deal");
    }
    return response.data.application;
  },
  /**
   * Browse controls for the deals table.
   *
   * Every field is optional and the server only pages when `page` or `limit` is
   * present, so callers that want the whole workspace (the portfolio store, the
   * kanban board) keep the behaviour they had.
   */
  getSimpleDealList: async (
    workspaceId: string,
    browse?: {
      page?: number;
      limit?: number;
      search?: string;
      sort?: string;
      dir?: "asc" | "desc";
      /** Saved view to scope the list to — a curated list's membership. */
      view?: string;
      /**
       * Ask for row data only. Drops the AI documents the table never reads —
       * tearsheets, summaries, chats, gap-analysis question sets — which is
       * most of the response by size. The detail page loads those itself via
       * `getDealById`.
       */
      lean?: boolean;
      /** Serialised FilterNode[] — see components/deals/filters.ts */
      filters?: string;
    }
  ) => {
    const response = await axiosInstance.get("/fetch_simple_deal_list", {
      params: {
        opportunity_id: workspaceId,
        ...(browse?.page ? { page: browse.page } : {}),
        ...(browse?.limit ? { limit: browse.limit } : {}),
        ...(browse?.search ? { search: browse.search } : {}),
        ...(browse?.sort ? { sort: browse.sort } : {}),
        ...(browse?.dir ? { dir: browse.dir } : {}),
        ...(browse?.view ? { view: browse.view } : {}),
        ...(browse?.lean ? { lean: 1 } : {}),
        ...(browse?.filters ? { filters: browse.filters } : {}),
      },
    });
    if (response.data.status !== "success") {
      throw new Error("Failed to fetch simple deal list");
    }

    const deals = response.data.data || [];

    // Debug logging for v2 data sufficiency
    deals.forEach((deal: any, index: number) => {
      if (deal.aiGenerations?.datasufficiencyscores_v2?.length > 0) {
        console.log(
          `Deal ${index} (${deal.company_name}) has v2 data sufficiency:`,
          deal.aiGenerations.datasufficiencyscores_v2
        );
      }
    });

    // Log raw gap_analysis question keys BEFORE Zod strips unknown fields
    const firstWithGap = deals.find((d: any) => d.aiGenerations?.gap_analysis?.length > 0);
    if (firstWithGap) {
      const rawQ = firstWithGap.aiGenerations.gap_analysis[0]?.questions?.[0];
      console.log('[RAW gap_analysis question keys]', rawQ ? Object.keys(rawQ) : 'no questions');
      console.log('[RAW gap_analysis question sample]', rawQ);
    }

    // `pagination` is present only when the caller asked to page; unpaginated
    // callers get it as undefined and can ignore it.
    return { deals, pagination: response.data.pagination as
      | { page: number; limit: number; total: number; total_pages: number }
      | undefined };
  },
  changeStatus: async (
    dealId: string,
    status: string,
    changedBy: string,
    workspaceId: string
  ): Promise<changeStatusResponse> => {
    const response = await axiosInstance.post("/edit_status", {
      applicant_company: dealId,
      application_status: status,
      changed_by: changedBy,
      pertains_to_opportunity: workspaceId,
    });
    return response.data;
  },
  bulkChangeStatus: async (
    workspaceId: string,
    dealIds: string[],
    status: string,
    changedBy: string
  ): Promise<changeStatusResponse> => {
    const response = await axiosInstance.post("/bulk_edit_status", {
      deal_ids: dealIds,
      new_status: status,
      changed_by: changedBy,
      workspace_id: workspaceId,
    });
    return response.data;
  },
  addDealNote: async (
    companyId: string,
    note: string,
    workspaceId: string,
    createdBy: string,
    tags?: string[]
  ): Promise<addDealNoteResponse> => {
    const response = await axiosInstance.post("/add_deal_note", {
      company_id: companyId,
      note: note,
      opportunity_id: workspaceId,
      created_by: createdBy,
      tags: tags || [],
    });
    return response.data;
  },
  editDealNote: async (
    noteId: string,
    note: string,
    workspaceId: string,
    companyId: string,
    createdBy: string,
    tags?: string[]
  ): Promise<addDealNoteResponse> => {
    const response = await axiosInstance.post("/edit_deal_note", {
      note_id: noteId,
      note: note,
      opportunity_id: workspaceId,
      company_id: companyId,
      created_by: createdBy,
      tags: tags || [],
    });
    return response.data;
  },
  deleteDealNote: async (
    noteId: string,
    workspaceId: string,
    companyId: string
  ): Promise<addDealNoteResponse> => {
    const response = await axiosInstance.post("/delete_deal_note", {
      note_id: noteId,
      opportunity_id: workspaceId,
      company_id: companyId,
    });
    return response.data;
  },
  fetchFlaggedCompanies: async (workspaceId: string) => {
    const response = await axiosInstance.get("/fetch_simple_deal_list", {
      params: {
        opportunity_id: workspaceId,
        archived: true,
      },
    });
    if (response.data.status !== "success") {
      throw new Error("Failed to fetch simple deal list");
    }
    return response.data.data || [];
  },
  removeFlaggedCompany: async (
    workspaceId: string,
    companyId: string
  ): Promise<DeleteFlaggedCompanyResponse> => {
    const response = await axiosInstance.post(
      `/remove_flag_from_company_in_opportunity`,
      {
        opportunity_id: workspaceId,
        company_id: companyId,
      }
    );
    return response.data;
  },
  bulkRemoveFlaggedCompany: async (
    workspaceId: string,
    companyIds: string[]
  ): Promise<DeleteFlaggedCompanyResponse> => {
    const response = await axiosInstance.post(
      `/bulk_remove_flag_from_companies_in_opportunity`,
      {
        opportunity_id: workspaceId,
        company_ids: companyIds,
      }
    );
    return response.data;
  },
};
export const createDeal = {
  createCompanyPitchbox: async (
    CreateCompanyPitchboxProps: CreateCompanyPitchboxProps
  ) => {
    const response = await axiosInstance.post("/create_company_pitchbox", {
      user_id: CreateCompanyPitchboxProps.userId,
      opportunity_id: CreateCompanyPitchboxProps.workspaceId,
      company_name: CreateCompanyPitchboxProps.companyName,
      company_website: CreateCompanyPitchboxProps.companyWebsite,
    });

    return response.data;
  },
  createOpportunityApplication: async (
    companyId: string,
    workspaceId: string,
    selectedStatus?: string,
    source?: string
  ) => {
    const response = await axiosInstance.post(
      "/create_opportunity_application",
      {
        applicant_company: companyId,
        pertains_to_opportunity: workspaceId,
        source: source || "",
        application_status: selectedStatus || "New",
      }
    );
    return response.data.result;
  },

  createFlaggedCompany: async (
    workspaceId: string,
    companyId: string,
    userId: string
  ): Promise<FlaggedCompanyResponse> => {
    const response = await axiosInstance.post(
      "/create_flagged_company_in_opportunity",
      {
        flagged_company: companyId,
        opportunity: workspaceId,
        flagged_by: userId,
      }
    );
    return response.data;
  },
  bulkCreateFlaggedCompany: async (
    workspaceId: string,
    companyIds: string[],
    userId: string
  ): Promise<FlaggedCompanyResponse> => {
    const response = await axiosInstance.post(
      "/bulk_create_flagged_companies_in_opportunity",
      {
        company_ids: companyIds,
        opportunity_id: workspaceId,
        flagged_by: userId,
      }
    );
    return response.data;
  },
  deleteFlaggedCompany: async (
    workspaceId: string,
    companyId: string
  ): Promise<DeleteFlaggedCompanyResponse> => {
    const response = await axiosInstance.post(
      "/delete_flagged_company_from_opportunity",
      {
        opportunity_id: workspaceId,
        company_id: companyId,
        applicant_rejection_email: {
          header: "Thank you for applying!",
          body: "We are sorry to inform you that we are not able to move forward with your application at this time.\n",
          email: "",
        },
        isStealth: true,
      }
    );
    return response.data;
  },
  bulkDeleteFlaggedCompany: async (
    workspaceId: string,
    companyIds: string[]
  ): Promise<DeleteFlaggedCompanyResponse> => {
    const response = await axiosInstance.post(
      "/bulk_delete_companies_from_opportunity",
      {
        opportunity_id: workspaceId,
        company_ids: companyIds,
        applicant_rejection_email: {
          header: "Thank you for applying!",
          body: "We are sorry to inform you that we are not able to move forward with your application at this time.\n",
          email: "",
        },
        isStealth: true,
      }
    );
    return response.data;
  },
};

interface EditDealInfoRequest {
  workspaceId: string;
  companyId: string;
  fieldName: string;
  fieldValue: string | number | boolean | string[] | Record<string, unknown>;
}

interface EditDealInfoResponse {
  status: string;
  message?: string;
  updatedApplication?: {
    applicant_company: string;
    pertains_to_opportunity: string;
    changed_by?: string;
    custom_text_a1?: string;
    custom_text_a2?: string;
    application_status?: string;
    action_type?: "pending" | "approved" | "declined" | "reviewed";
    created_on: Date;
    deal_info?: Array<{
      field_name: string;
      field_value: string | number | boolean;
      field_state: "approved" | "declined" | "pending";
    }>;
    fundraising?: {
      total_amount?: string;
      committed_amount?: string;
      lead_investor?: string;
    };
    website_summary?: string;
    deal_notes?: Array<{
      note: string;
      created_on: Date;
      created_by_user_id: string;
      created_by_user: string;
    }>;
    reports_in_progress?: {
      deal_memo: {
        type: boolean;
        updated_at: Date;
      };
    };
  };
  error?: string;
}

export const editDealInfo = {
  editDealInfo: async (
    EditDealInfoRequest: EditDealInfoRequest
  ): Promise<EditDealInfoResponse> => {
    const response = await axiosInstance.post("/edit_deal_info", {
      opportunity_id: EditDealInfoRequest.workspaceId,
      company_id: EditDealInfoRequest.companyId,
      field_name: EditDealInfoRequest.fieldName,
      field_value: EditDealInfoRequest.fieldValue,
      field_state: "approved",
    });
    return response.data;
  },
};

interface EditCompanyInfoRequest {
  companyId: string;
  fieldName: string;
  fieldValue: string | number | boolean | string[];
}

interface EditCompanyInfoResponse {
  status: string;
  message?: string;
  result?: {
    company_id: string;
    company_name: string;
    company_website: string;
    address: string;
    short_bio: string;
    business_model: string;
    industry: string[];
    stage: number;
  };
}

export const editCompanyInfo = {
  editCompanyInfo: async (
    EditCompanyInfoRequest: EditCompanyInfoRequest
  ): Promise<EditCompanyInfoResponse> => {
    const response = await axiosInstance.post(
      `/edit_company_during_application?company_id=${EditCompanyInfoRequest.companyId}`,
      {
        [EditCompanyInfoRequest.fieldName]: EditCompanyInfoRequest.fieldValue,
      }
    );
    return response.data;
  },
};

export const editSource = {
  editSource: async (
    companyId: string,
    workspaceId: string,
    source: string
  ) => {
    const response = await axiosInstance.post("/edit_source", {
      company_id: companyId,
      opportunity_id: workspaceId,
      source: source,
    });
    return response.data;
  },
};

interface WorkflowStatus {
  colour: string;
  title: string;
  description: string;
  order: number;
}

export interface StatusMigrationResult {
  from: string;
  to: string;
  modified_count: number;
}

export const editOpportunityStatuses = {
  editOpportunityStatuses: async (
    workspaceId: string,
    statuses: WorkflowStatus[],
    migrations?: Record<string, string>
  ): Promise<{
    result: unknown;
    migration_results: StatusMigrationResult[];
  }> => {
    const response = await axiosInstance.post("/edit_opportunity_statuses", {
      opportunity_id: workspaceId,
      statuses: statuses,
      ...(migrations ? { migrations } : {}),
    });

    if (response.data.status === "error") {
      throw new Error(response.data.message);
    }

    return {
      result: response.data.result,
      migration_results: response.data.migration_results ?? [],
    };
  },
};

/**
 * Deals per status. Without `pipeline`, every deal in the workspace, archived
 * included. With it, only the live deals the pipeline table's own view, search
 * and filters leave — what each stage tab would actually list.
 */
export const getStatusCounts = async (
  workspaceId: string,
  pipeline?: { view?: string; search?: string; filters?: string }
): Promise<Record<string, number>> => {
  const response = await axiosInstance.get("/get_status_counts", {
    params: {
      workspace_id: workspaceId,
      ...(pipeline ? { pipeline: 1, ...pipeline } : {}),
    },
  });

  if (response.data.status === "error") {
    throw new Error(response.data.message);
  }

  return response.data.counts ?? {};
};

interface ActivateDealResponse {
  status: string;
  message?: string;
  data: {
    tracked: number;
    maxLimit: number;
    newStatus?: string;
  };
}

export const activateDeal = async (
  workspaceId: string,
  dealId: string,
  nextStage?: string
): Promise<ActivateDealResponse> => {
  const response = await axiosInstance.post("/activate_deal", {
    opportunity_id: workspaceId,
    company_id: dealId,
    next_stage: nextStage,
  });

  if (response.data.status === "error") {
    throw new Error(response.data.message || "Failed to activate deal");
  }

  return response.data;
};

export interface DeepenDiligenceResponse {
  status: string;
  message: string;
  data: {
    diligence_level: "light" | "full";
    /** False when the deal already had full diligence — nothing was re-run. */
    regenerated: boolean;
  };
}

/** Take a light-screened deal to full diligence (SAAS-915). */
export const deepenDiligence = async (
  workspaceId: string,
  dealId: string
): Promise<DeepenDiligenceResponse> => {
  const response = await axiosInstance.post("/deepen_diligence", {
    opportunity_id: workspaceId,
    company_id: dealId,
  });

  if (response.data.status === "error") {
    throw new Error(response.data.message || "Failed to start full diligence");
  }

  return response.data;
};

export interface DeactivateDealResponse {
  status: string;
  message: string;
  data: {
    tracked: number;
  };
}

export const deactivateDeal = async (
  workspaceId: string,
  dealId: string
): Promise<DeactivateDealResponse> => {
  const response = await axiosInstance.post("/deactivate_deal", {
    opportunity_id: workspaceId,
    company_id: dealId,
  });

  if (response.data.status === "error") {
    throw new Error(response.data.message || "Failed to deactivate deal");
  }

  return response.data;
};

export interface SwapPremiumDealResponse {
  status: string;
  message: string;
}

export const swapPremiumDeal = async (
  workspaceId: string,
  dealId: string,
  demoteApplicationId: string
): Promise<SwapPremiumDealResponse> => {
  const response = await axiosInstance.post("/swap_premium_deal", {
    opportunity_id: workspaceId,
    company_id: dealId,
    demote_application_id: demoteApplicationId,
  });

  if (response.data.status === "error") {
    throw new Error(response.data.message || "Failed to swap premium deal");
  }

  return response.data;
};

// Founder invitation APIs
export const founderInvitationApi = {
  inviteFounderToCompany: async (
    companyId: string,
    founderEmail: string,
    founderName: string
  ) => {
    const response = await axiosInstance.post(
      `/company/${companyId}/invite-founder`,
      {
        founderEmail,
        founderName,
      }
    );
    return response.data;
  },

  getCompanyInvitationStatus: async (companyId: string) => {
    const response = await axiosInstance.get(
      `/company/${companyId}/invitation-status`
    );
    return response.data;
  },

  resendFounderInvitation: async (companyId: string) => {
    const response = await axiosInstance.post(
      `/company/${companyId}/resend-invitation`
    );
    return response.data;
  },

  cancelFounderInvitation: async (companyId: string) => {
    const response = await axiosInstance.post(
      `/company/${companyId}/cancel-invitation`
    );
    return response.data;
  },
};

/** Saved views for the deals table — the tab strip. Workspace-shared. */
export interface DealView {
  _id: string;
  name: string;
  order: number;
  mode: "filter" | "list";
  filter?: { field: string; op: string; value?: unknown }[];
  columns?: string[];
  sort?: { field: string; direction: "asc" | "desc" };
}

export interface AiColumn {
  _id: string;
  name: string;
  prompt: string;
  type: "text" | "number" | "currency" | "date" | "boolean";
  order: number;
  auto_run?: boolean;
}

export const dealViewsApi = {
  /**
   * Whole-set replace, matching the endpoint. Views carry their `_id` so an
   * edit updates rather than replaces — replacing would orphan every deal's
   * membership of a curated list.
   */
  save: async (workspaceId: string, views: Partial<DealView>[]) => {
    const response = await axiosInstance.post("/edit_opportunity_deal_views", {
      opportunity_id: workspaceId,
      views,
    });
    if (response.data.status === "error") {
      throw new Error(response.data.message);
    }
    return response.data.views as DealView[];
  },

  setMembership: async (
    workspaceId: string,
    viewId: string,
    companyIds: string[],
    action: "add" | "remove" = "add"
  ) => {
    const response = await axiosInstance.post("/deal_view_membership", {
      opportunity_id: workspaceId,
      view_id: viewId,
      company_ids: companyIds,
      action,
    });
    if (response.data.status === "error") {
      throw new Error(response.data.message);
    }
    return response.data as { matched: number; modified: number };
  },
};

export const aiColumnsApi = {
  save: async (workspaceId: string, columns: Partial<AiColumn>[]) => {
    const response = await axiosInstance.post("/edit_ai_columns", {
      opportunity_id: workspaceId,
      columns,
    });
    if (response.data.status === "error") {
      throw new Error(response.data.message);
    }
    return response.data.columns as AiColumn[];
  },

  /** Omit companyIds to run across the workspace, up to the server's cap. */
  run: async (workspaceId: string, columnId: string, companyIds?: string[]) => {
    const response = await axiosInstance.post("/run_ai_column", {
      opportunity_id: workspaceId,
      column_id: columnId,
      ...(companyIds ? { company_ids: companyIds } : {}),
    });
    if (response.data.status === "error") {
      throw new Error(response.data.message);
    }
    return response.data as { queued: number; capped?: boolean };
  },
};

/**
 * A fund-defined analysis: a name, an icon and ordered sections, each a title
 * and a prompt. Generated documents reference these by id, which is why the
 * save endpoint preserves ids rather than reassigning them.
 */
export interface AnalysisTemplateSection {
  _id?: string;
  title: string;
  prompt: string;
  order: number;
}

export interface AnalysisTemplate {
  _id?: string;
  name: string;
  icon?: string;
  order: number;
  sections: AnalysisTemplateSection[];
  copied_from?: string;
}

/** Read-only starter shipped in code, offered for duplication. */
export interface BuiltinAnalysisTemplate {
  id: string;
  name: string;
  icon: string;
  description: string;
  sections: { id: string; title: string; prompt: string; order: number }[];
}

export const analysisTemplatesApi = {
  list: async (workspaceId: string) => {
    const response = await axiosInstance.get("/analysis_templates", {
      params: { opportunity_id: workspaceId },
    });
    if (response.data.status === "error") {
      throw new Error(response.data.message);
    }
    return {
      templates: (response.data.templates ?? []) as AnalysisTemplate[],
      builtins: (response.data.builtins ?? []) as BuiltinAnalysisTemplate[],
    };
  },

  /**
   * Draft an analysis from a description of what the fund wants.
   *
   * Returns the draft and saves nothing — it goes into the editor for the fund to
   * change before they save it, because a template nobody read would silently
   * shape every memo they produce.
   */
  draft: async (workspaceId: string, description: string) => {
    const response = await axiosInstance.post("/draft_analysis_template", {
      opportunity_id: workspaceId,
      description,
    });
    if (response.data.status === "error") {
      throw new Error(response.data.message);
    }
    return response.data.template as AnalysisTemplate;
  },

  /** Whole-set replace, matching the endpoint. Send ids to keep documents attached. */
  save: async (workspaceId: string, templates: AnalysisTemplate[]) => {
    const response = await axiosInstance.post("/edit_analysis_templates", {
      opportunity_id: workspaceId,
      templates,
    });
    if (response.data.status === "error") {
      throw new Error(response.data.message);
    }
    return (response.data.templates ?? []) as AnalysisTemplate[];
  },
};
